import React from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  ChipProps,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import {
  People as PeopleIcon,
} from '@mui/icons-material';
import { User, Role } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';

interface DeleteUserDialogProps {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({
  open,
  user,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const { t } = useLanguage();
  
  const handleConfirm = async () => {
    await onConfirm();
  };

  const getRoleColor = (role: Role): ChipProps['color'] => {
    switch (role) {
      case Role.SUPER_ADMIN:
        return 'error';
      case Role.COMPANY_ADMIN:
        return 'warning';
      case Role.EMPLOYEE:
        return 'info';
      default:
        return 'default';
    }
  };

  if (!user) return null;

  return (
    <AppDialog presentation="confirm"
      open={open}
      onClose={(_, reason) => { if (!isLoading && reason !== 'backdropClick') onClose(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <DialogTitle>{t('users.deleteUser')}</DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t('users.deleteWarning')}
          </Typography>
        </Alert>

        <Box display="flex" alignItems="center" gap={2} p={2} bgcolor="action.hover" border="1px solid" borderColor="divider" borderRadius={2}>
          <PeopleIcon color="action" />
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
            <Box mt={1}>
              <Chip
                label={t(`role.${user.role}`)}
                color={getRoleColor(user.role)}
                size="small"
              />
            </Box>
          </Box>
        </Box>

        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('users.deleteQuestion')}
        </Typography>
        
        <Box component="ul" sx={{ mt: 1, mb: 0 }}>
          <li>{t('users.deleteConsequences.removeAccess')}</li>
          <li>{t('users.deleteConsequences.preserveData')}</li>
          <li>{t('users.deleteConsequences.cannotUndo')}</li>
          {user.role === Role.COMPANY_ADMIN && (
            <li><strong>{t('users.deleteConsequences.companyAdminWarning')}</strong></li>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap', gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={isLoading}
          variant="outlined"
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isLoading}
          variant="contained"
          color="error"
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? t('users.deleting') : t('users.deleteUser')}
        </Button>
      </DialogActions>
    </AppDialog>
  );
};

export default DeleteUserDialog;
